import { Component, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileItemComponent } from './file-item.component';
import { IconComponent } from '../icon/icon';
import { TranslatePipe } from '@ngx-translate/core';

let nextId = 0;

@Component({
  selector: 'wma-file-input',
  standalone: true,
  templateUrl: './file-input.component.html',
  host: { class: 'block' },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FileInputComponent,
      multi: true,
    },
  ],
  imports: [FileItemComponent, IconComponent, TranslatePipe],
})
export class FileInputComponent implements ControlValueAccessor {
  readonly accept = input<null | undefined | string>();
  readonly label = input('');
  readonly description = input('');
  readonly icon = input('');

  protected readonly inputId = `wma-file-input-${nextId++}`;

  protected $file = signal<File | null>(null);
  protected $dragging = signal(false);

  onChanged!: (file: File | null) => void;
  onTouched!: () => void;

  writeValue(file: File | null): void {
    this.$file.set(file);
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnChange(fn: () => void): void {
    this.onChanged = fn;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.$dragging.set(true);
  }

  onDragEnter(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.$dragging.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.$dragging.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.$dragging.set(false);

    const file = event.dataTransfer?.files[0];
    if (file && this.isFileTypeAllowed(file.type)) {
      this.setFile(file);
    }
  }

  onInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.item(0);
    if (file && this.isFileTypeAllowed(file.type)) {
      this.setFile(file);
    }
  }

  setFile(file: File) {
    this.$file.set(file);
    this.onChanged(file);
    this.onTouched();
  }

  clearFile() {
    this.$file.set(null);
    this.onChanged(null);
  }

  private isFileTypeAllowed(fileType: string): boolean {
    const accept = this.accept();
    if (!accept) {
      return true;
    }

    const allowedTypes = accept.split(',').map((type) => type.trim());
    return (
      accept.includes(fileType) ||
      allowedTypes.some((type) => fileType.startsWith(type.replace('*', '')))
    );
  }
}
